import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import InlineAbundanceRenderer from '../Chat/InlineAbundanceRenderer'
import Icon from '../../../../components/atoms/Icon'
import SydButton from '../../../../components/commonDesign/Button'
import FlexRow from '../../../../components/molecules/FlexRow'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import InlineActions from '../Chat/InlineActions'

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: 'var(--margin-sm)',
    paddingBottom: 'var(--padding-xs)',
    borderBottom: '1px solid var(--color-neutral-700)'
  },
  icon: {
    fontSize: '20px',
    color: 'var(--color-success-500)'
  },
  title: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--text-color-main)'
  },
  content: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--text-color-main)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.6,
    '& h1': {
      fontSize: 'var(--font-size-2xl)'
    },
    '& p': {
      margin: '0 0 12px 0',
      '&:last-child': {
        margin: 0
      }
    },
    '& ul, & ol': {
      margin: '0 0 12px 0',
      paddingLeft: '24px',
      '&:last-child': {
        margin: 0
      }
    },
    '& li': {
      marginBottom: '4px'
    },
    '& code': {
      backgroundColor: 'var(--surface-bg-color-d1)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'var(--font-family-monospace)'
    },
    '& pre': {
      border: 'var(--border-neutral-thin)',
      backgroundColor: 'var(--surface-bg-color-d2)',
      paddingLeft: 'var(--padding-sm)',
      paddingRight: 'var(--padding-sm)',
      borderRadius: '8px',
      overflow: 'auto',
      margin: '12px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0
      }
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-primary-500)',
      paddingLeft: '12px',
      margin: '12px 0',
      fontStyle: 'italic',
      color: 'var(--color-neutral-300)'
    },
    '& a': {
      color: 'var(--color-primary-500)',
      textDecoration: 'none',
      fontWeight: 500,
      borderBottom: '1px solid var(--color-primary-500)',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'var(--color-primary-400)',
        borderBottomColor: 'var(--color-primary-400)'
      },
      '&:visited': {
        color: 'var(--color-primary-600)',
        borderBottomColor: 'var(--color-primary-600)'
      }
    }
  },
  codeBlock: {
    position: 'relative',
    '& pre': {
      paddingTop: '36px'
    }
  },
  codeHeader: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  codeLanguage: {
    fontSize: '12px',
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase'
  }
}))

function FinalMessageBlock ({ content, timestamp, onSuggestionClick }) {
  const classes = useStyles()

  if (!content) {
    return null
  }

  const handleCodeAction = (code, language) => {
    if (language === 'dql' || language === 'sql') {
      // Open in query editor with query pre-filled
      const queryToolPath = '/admin/denali/analysis/catalog/query'
      // Store query in sessionStorage to pass to editor
      sessionStorage.setItem('denaliAssistantQuery', code)
      sessionStorage.setItem('denaliAssistantQueryType', language)
      window.open(queryToolPath, '_blank')
    } else {
      // Copy to clipboard
      navigator.clipboard.writeText(code)
      // TODO: Show toast notification
    }
  }

  // Custom renderers for markdown
  const renderers = {
    pre ({ children }) {
      // Extract the code element and its props
      const codeElement = children.props ? children : children[0]
      const className = codeElement.props?.className || ''
      const codeString = String(codeElement.props?.children || '').replace(/\n$/, '')

      // Check if this is an abundance engine chart
      // Use .* to capture the full language including slashes
      const match = /language-(.*)/.exec(className)
      const language = match ? match[1] : ''

      // Detect abundance engine charts and tables by multiple methods
      const isAbundanceChart =
        language === 'jsx-abundance-engine' ||
        (language.startsWith('jsx') && (codeString.includes('<InlineChart') || codeString.includes('<InlineTable'))) ||
        (codeString.includes('<InlineChart') && codeString.includes('options={{')) ||
        codeString.includes('<InlineTable')

      if (isAbundanceChart) {
        return <InlineAbundanceRenderer code={codeString} />
      }

      if (language === 'denali-ai-actions') {
        return <InlineActions content={codeString} onSuggestionClick={onSuggestionClick} />
      }

      if (language) {
        return (
          <div className={classes.codeBlock}>
            <div className={classes.codeHeader}>
              <span className={classes.codeLanguage}>{language.split('/')[0]}</span>
              <FlexRow gap='xs'>
                {(language === 'dql' || language === 'sql') ? (
                  <SydButton
                    size='xs'
                    variant='ghost'
                    onClick={() => handleCodeAction(codeString, language)}
                  >
                    <Icon name='open_in_new' style={{ fontSize: '14px' }} />
                    Open in Editor
                  </SydButton>
                ) : null}
                <SydButton
                  size='xs'
                  variant='ghost'
                  onClick={() => navigator.clipboard.writeText(codeString)}
                >
                  <Icon name='copy' style={{ fontSize: '14px' }} />
                  Copy
                </SydButton>
              </FlexRow>
            </div>
            <pre className={className}>
              <code>{codeString}</code>
            </pre>
          </div>
        )
      }

      // Fall back to default pre rendering for code blocks without language
      return <pre>{children}</pre>
    },
    code ({ inline, children, ...props }) {
      // Only handle inline code here
      if (inline) {
        return <code {...props}>{children}</code>
      }
      // Block code is handled by the pre renderer above
      return <code {...props}>{children}</code>
    }
  }

  return (
    <FadeIn className={classes.finalMessage}>
      <div className={classes.content}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={renderers}
        >
          {content}
        </ReactMarkdown>
      </div>
    </FadeIn>
  )
}

FinalMessageBlock.propTypes = {
  content: PropTypes.string.isRequired,
  timestamp: PropTypes.string,
  onSuggestionClick: PropTypes.func
}

export default FinalMessageBlock
